import "./UserAction.scss"
import "./Viewers.scss"

import React, { MouseEvent, useCallback } from "react"

import { scheduleApi, viewerApi } from "@shared/apis"
import { StudioPermissions } from "@shared/apis/studioPermissions"
import {
  Account,
  Schedule,
  useLayerPopupContext,
  useMessageSenderUpdater,
  Viewer,
} from "@shared/contexts"
import {maskName, trimAll} from "@shared/modules"
import {GROUP} from "../../../@shared/config";

const STREAMER_GROUP = ["STREAMER", "MANAGER"]

interface Props {
  viewer: Viewer
  account: Account
  schedule: Schedule | null
  closeAction: Function
  studioPermissions: StudioPermissions | undefined
}

function UserAction({
  viewer,
  account,
  schedule,
  closeAction,
  studioPermissions,
}: Props) {
  const { appendMessage } = useMessageSenderUpdater()
  const { open, alert, confirm } = useLayerPopupContext()

  const isStreamer = GROUP.STREAMER.includes(account.viewer?.role as string)
  const scheduleId = schedule?.id
  const streamerUsername = schedule?.user.username
  const isMe = account.viewer?.userId === viewer.userId
  const isBlockedChat = viewer.isBlockedChat
  const isStreaming = schedule?.streamingUsername === viewer.username
  const isAllowedBlockChat = studioPermissions?.isAllowedBlockChat
  const isAllowedKick = studioPermissions?.isAllowedKick

  const handleResponse = useCallback(
    (message?: string) => {
      if (message) {
        open("ALERT")
        alert.handleAlert({ contents: message })
      }
      closeAction()
    },
    [closeAction, open, alert],
  )

  const handleChangeStreaming = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      if (!scheduleId) return

      confirm.handleConfirm({
        key: viewer.userId,
        message: `${viewer.nickname} 권한${
          isStreamer && isStreaming ? "회수" : "이양"
        }을(를) 하시겠습니까?`,
        isConfirm: false,
        confirmAction: () => {
          scheduleApi
            .changeStreaming(
              scheduleId,
              isStreaming ? streamerUsername! : viewer.username,
            )
            .then(() => handleResponse())
            .catch(({ response }) => {
              const { data } = response
              handleResponse(data.message)
            })
        },
      })
      open("CONFIRM")
    },
    [
      confirm,
      open,
      viewer,
      scheduleId,
      isStreamer,
      isStreaming,
      streamerUsername,
      handleResponse,
    ],
  )

  const handleKick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()

      if (!isAllowedKick) return

      viewerApi
        .kick(scheduleId, viewer.username)
        .then(() => handleResponse())
        .catch(({ response }) => {
          const { data } = response
          handleResponse(data.message)
        })
    },
    [viewer, scheduleId, handleResponse, isAllowedKick],
  )

  const handleBlockingChat = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()

      if (!scheduleId || !isAllowedBlockChat) return

      if (isBlockedChat) {
        return viewerApi
          .unBlockingChat(scheduleId, viewer.username)
          .then(() => handleResponse())
          .catch(({ response }) => {
            debugger
            const { data } = response
            handleResponse(data.message)
          })
      }

      viewerApi
        .blockingChat(scheduleId, viewer.username)
        .then(() => handleResponse())
        .catch(({ response }) => {
          const { data } = response
          handleResponse(data.message)
        })
    },
    [viewer, scheduleId, isBlockedChat, handleResponse, isAllowedBlockChat],
  )

  return (
    <div className="dropdown-content">
      <div className="box-contents">
        <div className="header">
          <h1>{maskName(viewer.nickname, isStreamer, viewer.role)}</h1>
          <button className="close" onClick={() => closeAction()}>
            X
          </button>
        </div>
        <hr />
        {!isMe &&
          STREAMER_GROUP.includes(viewer.role) &&
          (isStreamer ? (
            <a href="#!" onClick={handleChangeStreaming}>
              권한{isStreaming ? "회수" : "이양"}
            </a>
          ) : (
            !isStreaming && (
              <a href="#!" onClick={handleChangeStreaming}>
                권한이양
              </a>
            )
          ))}

        {!STREAMER_GROUP.includes(viewer.role) && (
          <>
            {isAllowedKick && (
              <a href="#!" onClick={handleKick}>
                강제퇴장
              </a>
            )}
            {isAllowedBlockChat && (
              <a href="#!" onClick={handleBlockingChat}>
                채팅금지{isBlockedChat ? "해제" : ""}
              </a>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default UserAction
